import {
  Box,
  useTheme,
  TextField,
  Divider,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import { useStateContext } from "contexts/ContextProvider";
import React, { useState, useRef, useEffect } from "react";
import Grid from "@mui/material/Grid2";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { OVERLAY_NAMES } from "constants/Overlays";
import Autocomplete from "@mui/material/Autocomplete";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import CheckBoxIcon from "@mui/icons-material/CheckBoxRounded";
import Switch from "@mui/material/Switch";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { CustomPopper } from "components/CustomPopper";
import { useAuth } from "../../../contexts/AuthContext";
import api from "services/api";
import { usePageTitle } from "hooks/usePageTitle";
import { useWebSocket } from "contexts/WebSocketContext";

const splitLegacyAddress = (value) => {
  const normalized = typeof value === "string" ? value.trim() : "";
  if (!normalized) {
    return { line1: "", line2: "", line3: "" };
  }

  const newlineParts = normalized
    .split(/\r?\n/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (newlineParts.length >= 3) {
    return {
      line1: newlineParts[0],
      line2: newlineParts.slice(1, -1).join(", "),
      line3: newlineParts[newlineParts.length - 1],
    };
  }

  if (newlineParts.length === 2) {
    return {
      line1: newlineParts[0],
      line2: "",
      line3: newlineParts[1],
    };
  }

  const commaParts = normalized
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  if (commaParts.length >= 3) {
    return {
      line1: commaParts[0],
      line2: commaParts.slice(1, -1).join(", "),
      line3: commaParts[commaParts.length - 1],
    };
  }

  if (commaParts.length === 2) {
    return {
      line1: commaParts[0],
      line2: "",
      line3: commaParts[1],
    };
  }

  return { line1: normalized, line2: "", line3: "" };
};

const toNullableBoolean = (value) => {
  if (value === true) return true;
  if (value === false) return false;
  return null;
};

const LINGUIST_TIMEZONE_OPTIONS = [
  { value: "America/New_York", family: "ET" },
  { value: "America/Chicago", family: "CST/CDT (US Central)" },
  { value: "America/Denver", family: "MT" },
  { value: "America/Phoenix", family: "MST (Arizona)" },
  { value: "America/Los_Angeles", family: "PT" },
  { value: "America/Anchorage", family: "AKT" },
  { value: "Pacific/Honolulu", family: "HAT" },
  { value: "Europe/London", family: "UK" },
  { value: "Europe/Berlin", family: "CET/CEST (UTC+1)" },
  { value: "Etc/GMT-1", family: "UTC+1 (Fixed)" },
  { value: "Europe/Athens", family: "EET/EEST" },
  { value: "Asia/Dubai", family: "GST (UAE)" },
  { value: "Asia/Kolkata", family: "IST (India)" },
  { value: "Asia/Shanghai", family: "CST (China)" },
  { value: "Asia/Tokyo", family: "JST" },
  { value: "Australia/Sydney", family: "AET (Sydney)" },
  { value: "UTC", family: "UTC" },
];

const DEFAULT_LINGUIST_TIMEZONE = "America/New_York";

const getTimezoneAbbreviation = (timeZone, date = new Date()) => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "short",
    }).formatToParts(date);

    return (
      parts.find((part) => part.type === "timeZoneName")?.value ||
      String(timeZone)
    );
  } catch (error) {
    return String(timeZone);
  }
};

const getTimezoneOffsetMinutes = (timeZone, date = new Date()) => {
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });

    const parts = formatter.formatToParts(date).reduce((accumulator, part) => {
      if (part.type !== "literal") {
        accumulator[part.type] = part.value;
      }
      return accumulator;
    }, {});

    const asUtc = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour),
      Number(parts.minute),
      Number(parts.second),
    );

    return Math.round((asUtc - date.getTime()) / 60000);
  } catch (error) {
    return 0;
  }
};

const formatUtcOffset = (offsetMinutes) => {
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const absoluteMinutes = Math.abs(offsetMinutes);
  const hours = Math.floor(absoluteMinutes / 60);
  const minutes = absoluteMinutes % 60;

  if (!minutes) {
    return `UTC${sign}${hours}`;
  }

  return `UTC${sign}${hours}:${String(minutes).padStart(2, "0")}`;
};

const getLinguistTimezoneLabel = (timezoneOption, date = new Date()) => {
  const timeZone = timezoneOption?.value || String(timezoneOption || "UTC");
  const family = timezoneOption?.family || timeZone;
  const activeAbbreviation = getTimezoneAbbreviation(timeZone, date);
  const offsetLabel = formatUtcOffset(getTimezoneOffsetMinutes(timeZone, date));

  return `${family} (currently ${activeAbbreviation}) - ${offsetLabel}`;
};

const getInitialRequirementValues = (linguist, requirementDefinitions = []) => {
  const requirementMap = { ...(linguist?.RequirementMap || {}) };
  const requirements = linguist?.Requirements || linguist?.RequirementValues || [];

  requirements.forEach((requirement) => {
    const sourceColumn = requirement?.Definition?.SourceColumn || requirement?.SourceColumn;
    if (sourceColumn) {
      requirementMap[sourceColumn] = toNullableBoolean(requirement.Value);
    }
  });

  const values = {};
  requirementDefinitions.forEach(({ SourceColumn }) => {
    const fallback = toNullableBoolean(linguist?.[SourceColumn]);
    values[SourceColumn] =
      typeof requirementMap[SourceColumn] !== "undefined"
        ? toNullableBoolean(requirementMap[SourceColumn])
        : fallback;
  });

  return values;
};

const getRequirementDefinitionsForForm = (
  linguist,
  dynamicDefinitions = [],
) => {
  const mergedDefinitions = new Map();

  dynamicDefinitions.forEach((definition) => {
    const sourceColumn = definition?.SourceColumn;
    if (!sourceColumn) return;

    mergedDefinitions.set(sourceColumn, {
      Key: definition?.Key || sourceColumn,
      Name: definition?.Name || sourceColumn,
      SourceColumn: sourceColumn,
    });
  });

  const requirements = linguist?.Requirements || linguist?.RequirementValues || [];
  requirements.forEach((requirement) => {
    const sourceColumn =
      requirement?.Definition?.SourceColumn || requirement?.SourceColumn;
    if (!sourceColumn) return;

    const name = requirement?.Definition?.Name || requirement?.Name || sourceColumn;
    const key = requirement?.Definition?.Key || requirement?.Key || sourceColumn;

    if (!mergedDefinitions.has(sourceColumn)) {
      mergedDefinitions.set(sourceColumn, {
        Key: key,
        Name: name,
        SourceColumn: sourceColumn,
      });
    }
  });

  return Array.from(mergedDefinitions.values()).sort((left, right) =>
    String(left?.Name || "").localeCompare(String(right?.Name || "")),
  );
};

const LinguistNewEdit = () => {
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { overlayData, setOverlayData, setOverlayName, triggerPageRefresh } =
    useStateContext();
  usePageTitle(
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? "New Linguist"
      : "Edit Linguist",
  );

  const linguistLanguages =
    overlayData.Linguist &&
    overlayData.Linguist.LinguistLanguages.map((ling) => ({
      id: ling.Language.id,
      Name: ling.Language.Name,
      Rate: ling.Rate,
      Mode: ling.Mode,
    }));

  const [requirementDefinitions, setRequirementDefinitions] = useState(() =>
    getRequirementDefinitionsForForm(overlayData.Linguist, []),
  );

  const [selectedLanguages, setSelectedLanguages] = useState(
    linguistLanguages || [],
  );

  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [isEditable, setIsEditable] = useState(true);

  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [addressError, setAddressError] = useState(false);
  const [phoneNumberError, setPhoneNumberError] = useState(false);
  const [rateError, setRateError] = useState(false);
  const [modeError, setModeError] = useState(false);

  const [useMultipleRates, setUseMultipleRates] = useState(
    linguistLanguages ? linguistLanguages.length > 1 : false,
  );

  const [rate, setRate] = useState(
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? ""
      : linguistLanguages && linguistLanguages.length > 0
        ? linguistLanguages[0].Rate || ""
        : "",
  );

  // Track original rate to detect changes
  const [originalRate] = useState(
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? null
      : linguistLanguages && linguistLanguages.length > 0
        ? linguistLanguages[0].Rate || null
        : null,
  );

  const [mode, setMode] = useState(
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? "Consecutive"
      : linguistLanguages && linguistLanguages.length > 0
        ? linguistLanguages[0].Mode || "Consecutive"
        : "",
  );

  const [minimumHours, setMinimumHours] = useState(
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? 1
      : overlayData.Linguist
        ? overlayData.Linguist.MinimumHours
        : 1,
  );

  const isAvailableInPerson =
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? false
      : overlayData.Linguist.LinguistLanguages
        ? overlayData.Linguist.IsAvailableInPerson
        : false;

  const isAvailableVirtual =
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? false
      : overlayData.Linguist.LinguistLanguages
        ? overlayData.Linguist.IsAvailableVirtual
        : false;

  const isAvailablePhone =
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? false
      : overlayData.Linguist.LinguistLanguages
        ? overlayData.Linguist.IsAvailablePhone
        : false;

  const [availableTypes, setAvailableTypes] = useState([
    {
      Name: "Phone",
      IsAvailable: isAvailablePhone,
    },
    {
      Name: "Virtual",
      IsAvailable: isAvailableVirtual,
    },
    {
      Name: "In Person",
      IsAvailable: isAvailableInPerson,
    },
  ]);

  const updateAvailability = (name, isAvailable) => {
    setAvailableTypes((prevTypes) =>
      prevTypes.map((type) =>
        type.Name === name ? { ...type, IsAvailable: isAvailable } : type,
      ),
    );
  };

  const [name, setName] = useState(
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? ""
      : overlayData.Linguist.Name,
  );
  const [email, setEmail] = useState(
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? ""
      : overlayData.Linguist.Email,
  );

  const initialAddress =
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? { line1: "", line2: "", line3: "" }
      : {
          line1:
            overlayData.Linguist.AddressLine1 ||
            splitLegacyAddress(overlayData.Linguist.Address).line1,
          line2:
            overlayData.Linguist.AddressLine2 ||
            splitLegacyAddress(overlayData.Linguist.Address).line2,
          line3:
            overlayData.Linguist.AddressLine3 ||
            splitLegacyAddress(overlayData.Linguist.Address).line3,
        };

  const [addressLine1, setAddressLine1] = useState(initialAddress.line1);
  const [addressLine2, setAddressLine2] = useState(initialAddress.line2);
  const [addressLine3, setAddressLine3] = useState(initialAddress.line3);

  const [phoneNumber, setPhoneNumber] = useState(
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? ""
      : overlayData.Linguist.PhoneNumber,
  );

  const [status, setStatus] = useState(
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? ""
      : overlayData.Linguist.Status,
  );

  const [certification, setCertification] = useState(
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? "N/A"
      : overlayData.Linguist.Certifications,
  );

  const [active, setActive] = useState(
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? false
      : overlayData.Linguist.Active || false,
  );
  const [linguistTimezone, setLinguistTimezone] = useState(
    DEFAULT_LINGUIST_TIMEZONE,
  );
  const selectedTimezone =
    String(linguistTimezone || "").trim() || DEFAULT_LINGUIST_TIMEZONE;
  const timezoneOptions =
    LINGUIST_TIMEZONE_OPTIONS.some((option) => option.value === selectedTimezone)
      ? LINGUIST_TIMEZONE_OPTIONS
      : [
          ...LINGUIST_TIMEZONE_OPTIONS,
          { value: selectedTimezone, family: selectedTimezone },
        ];
  const [requirementValues, setRequirementValues] = useState(() =>
    getInitialRequirementValues(
      overlayData.Linguist,
      getRequirementDefinitionsForForm(overlayData.Linguist, []),
    ),
  );

  useEffect(() => {
    let isMounted = true;

    const syncRequirementDefinitions = async () => {
      try {
        const response = await api.get("/linguists/requirements");
        if (!isMounted) return;

        const fromDb = Array.isArray(response?.data) ? response.data : [];
        const mergedDefinitions = getRequirementDefinitionsForForm(
          overlayData.Linguist,
          fromDb,
        );

        setRequirementDefinitions(mergedDefinitions);
        setRequirementValues((previous) => {
          const initialValues = getInitialRequirementValues(
            overlayData.Linguist,
            mergedDefinitions,
          );
          const nextValues = { ...initialValues };

          Object.keys(previous || {}).forEach((sourceColumn) => {
            if (Object.prototype.hasOwnProperty.call(nextValues, sourceColumn)) {
              nextValues[sourceColumn] = previous[sourceColumn];
            }
          });

          return nextValues;
        });
      } catch (error) {
        if (!isMounted) return;

        const fallbackDefinitions = getRequirementDefinitionsForForm(
          overlayData.Linguist,
          [],
        );
        setRequirementDefinitions(fallbackDefinitions);
        setRequirementValues(
          getInitialRequirementValues(overlayData.Linguist, fallbackDefinitions),
        );
      }
    };

    syncRequirementDefinitions();

    return () => {
      isMounted = false;
    };
  }, [overlayData.Linguist]);

  useEffect(() => {
    let isMounted = true;

    const loadLinguistTimezone = async () => {
      if (overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW) {
        setLinguistTimezone(DEFAULT_LINGUIST_TIMEZONE);
        return;
      }

      const linguistId = overlayData?.Linguist?.id;
      if (!linguistId) {
        setLinguistTimezone(DEFAULT_LINGUIST_TIMEZONE);
        return;
      }

      try {
        const response = await api.get(
          `/linguists/${linguistId}/notification-preferences`,
        );

        if (!isMounted) return;

        const timezone = String(response?.data?.Timezone || "").trim();
        setLinguistTimezone(timezone || DEFAULT_LINGUIST_TIMEZONE);
      } catch (error) {
        if (!isMounted) return;
        setLinguistTimezone(DEFAULT_LINGUIST_TIMEZONE);
      }
    };

    loadLinguistTimezone();

    return () => {
      isMounted = false;
    };
  }, [overlayData.Action, overlayData.Linguist]);

  const [, setCertificationOther] = useState(
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? ""
      : overlayData.Linguist.Certifications || "",
  );

  const otherCertificationRef = useRef(null);
  const languagesRef = useRef(null);

  const saveLinguistTimezonePreference = async (linguistId) => {
    if (!linguistId) return;

    await api.put(`/linguists/${linguistId}/notification-preferences`, {
      Timezone:
        String(linguistTimezone || "").trim() || DEFAULT_LINGUIST_TIMEZONE,
    });
  };

  const handleRequirementToggle = (sourceColumn, checked) => {
    setRequirementValues((previous) => ({
      ...previous,
      [sourceColumn]: checked,
    }));
  };

  function saveChanges() {
    // Validation
    if (!name || name.trim() === "") {
      setNameError(true);
      alert("Please enter a linguist name.");
      return;
    }

    if (!email || email.trim() === "") {
      setEmailError(true);
      alert("Please enter an email address.");
      return;
    }

    // Reset errors
    setNameError(false);
    setEmailError(false);

    setIsSavingChanges(true);
    setIsEditable(false);

    const data = {
      LinguistID: overlayData.LinguistID,
      Linguist: {
        Name: name,
        Email: email,
        Address: [addressLine1, addressLine2, addressLine3]
          .map((part) => (typeof part === "string" ? part.trim() : ""))
          .filter(Boolean)
          .join(", "),
        AddressLine1: addressLine1?.trim() || null,
        AddressLine2: addressLine2?.trim() || null,
        AddressLine3: addressLine3?.trim() || null,
        PhoneNumber: phoneNumber,
        Status: status || null,
        Certifications: certification,
        MinimumHours: minimumHours || 1,
        IsAvailablePhone: availableTypes[0].IsAvailable,
        IsAvailableVirtual: availableTypes[1].IsAvailable,
        IsAvailableInPerson: availableTypes[2].IsAvailable,
        Active: active,
      },

      Requirements: requirementDefinitions.map((definition) => ({
        ...definition,
        Value: toNullableBoolean(requirementValues[definition.SourceColumn]),
      })),

      LinguistLanguages: selectedLanguages,

      MemberID: currentUser.id,
    };

    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? addNewLinguist(data)
      : updateLinguist(data);
  }

  function addNewLinguist(data) {
    api
      .post("/linguists/addNewLinguist", {
        data: data,
      })
      .then(async (response) => {
        setIsSavingChanges(false);
        setIsEditable(true);

        if (response.status === 201 || response.data) {
          const createdLinguistId =
            response?.data?.linguist?.id || response?.data?.id || null;
          let timezoneSaveError = null;

          if (createdLinguistId) {
            try {
              await saveLinguistTimezonePreference(createdLinguistId);
            } catch (error) {
              timezoneSaveError = error;
            }
          }

          triggerPageRefresh("New Linguist Added Successfully!", theme);

          if (timezoneSaveError) {
            alert(
              "Linguist was saved, but timezone could not be updated. Please retry from the linguist profile.",
            );
          }

          // Emit socket events to notify other users
          if (socket) {
            socket.emit("assignment:saved", {
              modelType: "Linguist",
              recordId: createdLinguistId || response.data.id,
              user: {
                id: currentUser.id,
                name: currentUser.Name,
                imageThumbnail: currentUser.ImageThumbnail,
              },
              projectNumber: response?.data?.linguist?.Name || response.data.Name,
            });

            // Also notify the linguists list page to refetch
            socket.emit("assignment:saved", {
              modelType: "LinguistsList",
              recordId: "all",
              user: {
                id: currentUser.id,
                name: currentUser.Name,
                imageThumbnail: currentUser.ImageThumbnail,
              },
              projectNumber: response?.data?.linguist?.Name || response.data.Name,
            });
          }

          setOverlayName("");
        }
      })
      .catch((err) => {
        setIsSavingChanges(false);
        setIsEditable(true);

        const errorMessage =
          err.response?.data?.error ||
          err.message ||
          "Failed to add linguist. Please try again.";
        alert(`Error: ${errorMessage}`);
      });
  }

  function updateLinguist(data) {
    api
      .post("/linguists/updateLinguist", {
        data: data,
      })
      .then(async (response) => {
        setIsSavingChanges(false);
        setIsEditable(true);

        if (response.status === 201 || response.data) {
          let timezoneSaveError = null;
          try {
            await saveLinguistTimezonePreference(overlayData?.Linguist?.id);
          } catch (error) {
            timezoneSaveError = error;
          }

          triggerPageRefresh("Linguist Updated Successfully!", theme);

          if (timezoneSaveError) {
            alert(
              "Linguist was updated, but timezone could not be updated. Please retry from the linguist profile.",
            );
          }

          // Emit socket events to notify other users
          if (socket) {
            socket.emit("assignment:saved", {
              modelType: "Linguist",
              recordId: overlayData.Linguist.id,
              user: {
                id: currentUser.id,
                name: currentUser.Name,
                imageThumbnail: currentUser.ImageThumbnail,
              },
              projectNumber: overlayData.Linguist.Name,
            });

            // Also notify the linguists list page to refetch
            socket.emit("assignment:saved", {
              modelType: "LinguistsList",
              recordId: "all",
              user: {
                id: currentUser.id,
                name: currentUser.Name,
                imageThumbnail: currentUser.ImageThumbnail,
              },
              projectNumber: overlayData.Linguist.Name,
            });
          }

          // Check if rate has changed and automatically open rate update overlay
          const currentRate = parseFloat(rate || 0);
          const oldRate = parseFloat(originalRate || 0);
          const rateChanged = Math.abs(currentRate - oldRate) > 0.01;

          if (rateChanged && rate) {
            // Rate has changed - open the rate update overlay
            const rateUpdateData = {
              LinguistID: overlayData.Linguist.id,
              LinguistName: name,
              NewRate: rate,
            };
            setOverlayData(rateUpdateData);
            setOverlayName(OVERLAY_NAMES.LINGUIST_RATE_UPDATE);
          } else {
            // No rate change - just close the overlay
            setOverlayName("");
          }
        }
      })
      .catch((err) => {
        console.error("Error updating linguist:", err);
        setIsSavingChanges(false);
        setIsEditable(true);

        const errorMessage =
          err.response?.data?.error ||
          err.message ||
          "Failed to update linguist. Please try again.";
        alert(`Error: ${errorMessage}`);
      });
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: theme.palette.background.default,
          width: { xs: "100%", sm: "90%", md: "75%", lg: "60%" },
          minWidth: { xs: "100%", sm: "600px" },
          maxWidth: "1200px",
          height: { xs: "95%", sm: "75%" },
          display: "flex",
          borderRadius: "1.5rem",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          color: theme.palette.text.primary,
          p: "0rem .2rem 1.5rem 0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
            p: "0.5rem 2rem",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              width: "60%",
              flexDirection: "row",
              gap: 1,
            }}
          >
            <IconButton
              color="primary"
              size="large"
              onClick={() => setOverlayName("")}
            >
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>

            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
              {overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
                ? "New Linguist"
                : "Edit Linguist"}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "flex-end",
              width: "40%",
              flexDirection: "row",
            }}
          >
            {isMobile ? (
              <Tooltip title="Discard" placement="top">
                <IconButton
                  color="primary"
                  onClick={() => setOverlayName("")}
                  sx={{
                    width: "40px",
                    height: "40px",
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: "12px",
                  }}
                >
                  <DisabledByDefaultRoundedIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                color="primary"
                variant="outlined"
                size="large"
                endIcon={<DisabledByDefaultRoundedIcon />}
                onClick={() => setOverlayName("")}
              >
                Discard
              </Button>
            )}

            <Box p="0 .5rem" />

            {isMobile ? (
              <Tooltip title="Save" placement="top">
                <span>
                  <IconButton
                    disabled={
                      !useMultipleRates
                        ? !rate || rate === ""
                        : selectedLanguages.length === 0 ||
                          selectedLanguages.some(
                            (lang) => !lang.Rate || lang.Rate === "",
                          )
                    }
                    color="primary"
                    onClick={saveChanges}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.background.default,
                      borderRadius: "12px",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                      },
                      "&.Mui-disabled": {
                        backgroundColor:
                          theme.palette.action.disabledBackground,
                        color: theme.palette.action.disabled,
                      },
                    }}
                  >
                    <SaveRoundedIcon />
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <Button
                loading={isSavingChanges}
                disabled={
                  !useMultipleRates
                    ? !rate || rate === ""
                    : selectedLanguages.length === 0 ||
                      selectedLanguages.some(
                        (lang) => !lang.Rate || lang.Rate === "",
                      )
                }
                color="primary"
                variant="contained"
                size="large"
                endIcon={<SaveRoundedIcon />}
                onClick={saveChanges}
                sx={{
                  borderRadius: "12px",
                }}
              >
                Save
              </Button>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            p: "1rem 2.3rem 1rem 2.5rem",
            height: "100%",
            bgcolor: theme.palette.background.alt,
            border: `1px solid ${theme.palette.background.default}`,
            borderRadius: "1.5rem",
            overflowY: "auto",
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
            },
          }}
        >
          <Grid container spacing={2}>
            <Grid
              size={{ xs: 12 }}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h4" fontWeight="bold">
                Account Information
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    disabled={!isEditable}
                    checked={active}
                    onChange={(event) => setActive(event.target.checked)}
                  />
                }
                label="Active"
                labelPlacement="start"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id="Name"
                label="Name"
                //defaultValue={name || "N/A"}
                error={nameError}
                value={name || ""}
                onChange={(event) => {
                  setName(event.target.value);
                  setNameError(event.target.value.trim() === "");
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id="Email"
                label="Email"
                //defaultValue={email || "N/A"}
                error={emailError}
                value={email || ""}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setEmailError(event.target.value.trim() === "");
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id="AddressLine1"
                label="Address Line 1"
                error={addressError}
                value={addressLine1 || ""}
                onChange={(event) => {
                  setAddressLine1(event.target.value);
                  setAddressError(event.target.value.trim() === "");
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id="AddressLine2"
                label="Address Line 2 (Optional)"
                value={addressLine2 || ""}
                onChange={(event) => {
                  setAddressLine2(event.target.value);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id="AddressLine3"
                label="City, State ZIP"
                value={addressLine3 || ""}
                onChange={(event) => {
                  setAddressLine3(event.target.value);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id="PhoneNumber"
                label="Phone Number"
                //defaultValue={phoneNumber || "N/A"}
                error={phoneNumberError}
                value={phoneNumber || ""}
                onChange={(event) => {
                  setPhoneNumber(event.target.value);
                  setPhoneNumberError(event.target.value.trim() === "");
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                select
                id="LinguistStatus"
                label="Linguist Status"
                //defaultValue={status || "N/A"}
                value={status || ""}
                onChange={(event) => {
                  setStatus(event.target.value);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              >
                {overlayData.LinguistStatuses.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                //multiline
                id="MinHours"
                label="Minimum Hours"
                //defaultValue={notes || "N/A"}
                value={minimumHours || 1}
                onChange={(event) => {
                  setMinimumHours(event.target.value);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                select
                id="LinguistTimezone"
                label="Timezone"
                value={linguistTimezone || DEFAULT_LINGUIST_TIMEZONE}
                onChange={(event) => {
                  setLinguistTimezone(event.target.value);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              >
                {timezoneOptions.map((timezoneOption) => (
                  <MenuItem
                    key={timezoneOption.value}
                    value={timezoneOption.value}
                  >
                    {getLinguistTimezoneLabel(timezoneOption)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider />
            </Grid>
            <Grid container size={{ xs: 12 }} ref={languagesRef}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="h5" fontWeight="bold">
                  Languages
                </Typography>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  slots={{
                    popper: CustomPopper, // Use the custom Popper component
                  }}
                  multiple
                  size="small"
                  id="checkboxes-tags-demo"
                  options={overlayData.Languages}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option.Name}
                  //getoptionselected={(option, value) => option.id === value.id} // Add this line
                  value={selectedLanguages}
                  onChange={(event, newValue) => {
                    const occurrenceMap = newValue.reduce((acc, item) => {
                      acc[item.id] = (acc[item.id] || 0) + 1;
                      return acc;
                    }, {});

                    const uniqueValues = newValue.filter(
                      (item) => occurrenceMap[item.id] === 1,
                    );

                    // Add a default Rate and Mode to items without them
                    const updatedValues = uniqueValues.map((item) => ({
                      ...item,
                      Rate: item.Rate || rate || 0,
                      Mode: item.Mode || mode || "", // Ensure Mode is always set
                    }));

                    setSelectedLanguages(updatedValues);
                  }}
                  renderOption={(props, option, { selected }) => {
                    const { key, ...optionProps } = props;
                    const isSelected = selectedLanguages.some(
                      (lang) => lang.id === option.id,
                    ); // Check if the option is selected
                    return (
                      <li key={key} {...optionProps}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={isSelected}
                          //onDelete={(event) => {
                          //  event.stopPropagation();
                          //  handleDelete(option);
                          //}}
                        />
                        {option.Name}
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Languages"
                      placeholder="Type or select from the list"
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid container size={{ xs: 12 }}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="h5" fontWeight="bold">
                  Rates
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  value="end"
                  control={
                    <Switch
                      checked={!useMultipleRates}
                      onChange={(checked) => {
                        setUseMultipleRates(!useMultipleRates);

                        setSelectedLanguages((prevLanguages) =>
                          prevLanguages.map((lang) => ({
                            ...lang,
                            Rate: rate,
                            Mode: mode, // Ensure Mode is updated when switching rate modes
                          })),
                        );
                      }}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  }
                  label={
                    useMultipleRates
                      ? "Use a different rate for each language."
                      : "Use a single rate for all languages."
                  }
                  labelPlacement="end"
                />
              </Grid>
            </Grid>

            {!useMultipleRates ? (
              <Grid container size={{ xs: 12 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    disabled={!isEditable}
                    size="small"
                    id="Rate"
                    label="Rate For All Languages"
                    type="number"
                    //defaultValue={rate || "N/A"}
                    error={rateError}
                    value={rate}
                    onChange={(event) => {
                      const inputValue = event.target.value;
                      const newRate =
                        inputValue === "" ? "" : parseFloat(inputValue);

                      setRate(isNaN(newRate) ? "" : newRate);
                      setRateError(inputValue.trim() === "");

                      setSelectedLanguages((prevLanguages) =>
                        prevLanguages.map((lang) => ({
                          ...lang,
                          Rate: isNaN(newRate) ? "" : newRate,
                          Mode: lang.Mode || mode, // Preserve existing Mode or use default
                        })),
                      );
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                        },
                        "&.MuiInputBase-root fieldset": {
                          py: 0,
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    disabled={!isEditable}
                    size="small"
                    select
                    id="Mode"
                    label="Mode For All Languages"
                    //defaultValue={rate || "N/A"}
                    error={modeError}
                    value={mode}
                    onChange={(event) => {
                      const newMode = event.target.value;
                      setMode(newMode);
                      setModeError(event.target.value.trim() === "");

                      // Update Mode for all languages when in single rate mode
                      if (!useMultipleRates) {
                        setSelectedLanguages((prevLanguages) =>
                          prevLanguages.map((lang) => ({
                            ...lang,
                            Mode: newMode,
                          })),
                        );
                      }
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                        },
                        "&.MuiInputBase-root fieldset": {
                          py: 0,
                        },
                      },
                    }}
                  >
                    {overlayData.LanguageModes.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            ) : (
              selectedLanguages.map((language, index) => (
                <Grid
                  key={language + "*-*" + index}
                  container
                  size={{ xs: 12 }}
                >
                  <Grid
                    size={{ xs: 12, md: 6 }}
                    key={language.Name + "-RATE-" + language.id}
                  >
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      id="Rate"
                      label={`Rate For: ${language.Name}`}
                      type="number"
                      value={language.Rate}
                      onChange={(event) => {
                        const inputValue = event.target.value;
                        const newRate =
                          inputValue === "" ? "" : parseFloat(inputValue);
                        setSelectedLanguages((prevLanguages) =>
                          prevLanguages.map((lang) =>
                            lang.id === language.id
                              ? { ...lang, Rate: isNaN(newRate) ? "" : newRate }
                              : lang,
                          ),
                        );
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{ xs: 12, md: 6 }}
                    key={language.Name + "-MODE-" + language.id}
                  >
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      select
                      id="Mode"
                      label={`Mode For: ${language.Name}`}
                      value={language.Mode}
                      onChange={(event) => {
                        const newMode = event.target.value;
                        setSelectedLanguages((prevLanguages) =>
                          prevLanguages.map((lang) =>
                            lang.id === language.id &&
                            lang.Rate === language.Rate
                              ? { ...lang, Mode: newMode }
                              : lang,
                          ),
                        );
                      }}
                    >
                      {overlayData.LanguageModes.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              ))
            )}
            <Grid size={{ xs: 12 }}>
              <Typography variant="h4" fontWeight="bold">
                Service Types
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <List
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  padding: 0,
                  overflowX: "auto",
                  height: "100%",
                  width: "100%",
                }}
              >
                {availableTypes.map((availableType) => {
                  const labelId = `checkbox-list-label-${availableType}`;

                  return (
                    <ListItem key={availableType.Name} disablePadding>
                      <ListItemButton
                        role={undefined}
                        onClick={() =>
                          updateAvailability(
                            availableType.Name,
                            !availableType.IsAvailable,
                          )
                        }
                        dense
                        disabled={!isEditable}
                      >
                        <ListItemIcon>
                          <Checkbox
                            disabled={!isEditable}
                            edge="start"
                            checked={availableType.IsAvailable}
                            tabIndex={-1}
                            inputProps={{ "aria-labelledby": labelId }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          id={labelId}
                          disableTypography
                          primary={
                            <Typography variant="body1">
                              {availableType.Name}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Divider />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h4" fontWeight="bold">
                Documents & Requirements
              </Typography>
            </Grid>
            {requirementDefinitions.map((requirementDefinition) => (
              <Grid
                key={requirementDefinition.SourceColumn}
                size={{ xs: 12, md: 4 }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={!isEditable}
                      checked={
                        toNullableBoolean(
                          requirementValues[requirementDefinition.SourceColumn],
                        ) === true
                      }
                      onChange={(event) =>
                        handleRequirementToggle(
                          requirementDefinition.SourceColumn,
                          event.target.checked,
                        )
                      }
                    />
                  }
                  label={requirementDefinition.Name}
                />
              </Grid>
            ))}
            <Grid size={{ xs: 12 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                //select
                id="Certification"
                label="Certification"
                value={certification}
                onFocus={(event) => {
                  if (!certification) {
                    setCertification("");
                  }
                }}
                onBlur={(event) => {
                  if (!event.target.value || event.target.value.trim() === "") {
                    setCertification("N/A");
                  }
                }}
                onChange={(event) => {
                  const newValue = event.target.value;
                  setCertification(newValue);

                  if (newValue === "Other Certification") {
                    setTimeout(() => {
                      otherCertificationRef.current.scrollIntoView({
                        behavior: "smooth",
                      });
                    }, 250);
                  } else {
                    setCertificationOther("");
                  }
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              ></TextField>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default LinguistNewEdit;
